import Cancel from '@mui/icons-material/Cancel';
import { AlertColor, Box, Button, TextField, Typography, Select, MenuItem } from '@mui/material';
import { useRef, useState } from 'react';
import strings from '../../../../common/Translation/Translate';
import { FormDialog } from '../../../../components/Dialog/FormDialog';
import { bookingDetailToolbarButtonSx } from '../bookingDetailToolbarSx';
import Notification from '../../../../components/Notification';
import axiosInstance from '../../../../utils/axios';
import { CANCEL_REASONS } from '../../../../utils/constants/data';
import { URLS } from '../../../../utils/constants/urls';

type CancelReasonOption = { id: string; name: string };

type CancelButtonProps = {
  id: any;
  onSuccess: () => void;
  /** Render a plain menu row instead of the contained toolbar button. */
  compact?: boolean;
  /** Callback fired when the trigger is clicked (e.g. to close the parent menu). */
  onTriggerClick?: () => void;
};
export const CancelButton: React.FC<CancelButtonProps> = ({
  id,
  onSuccess,
  compact,
  onTriggerClick,
}) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT. */
  const savingRef = useRef(false);
  const [cancelReasonData, setCancelReasonData] = useState<CancelReasonOption[]>([]);
  const reasonsLoadedRef = useRef(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [error, setError] = useState<{ error: boolean; errorType: string; errorMsg: any }>({
    error: false,
    errorType: '',
    errorMsg: '',
  });
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const maxLength = 250;

  const openCancelDialog = async () => {
    onTriggerClick?.();
    setOpenDialog(true);
    if (reasonsLoadedRef.current) return;
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.cancelBookingDropdown);
      setCancelReasonData(Array.isArray(data) ? data : []);
      reasonsLoadedRef.current = true;
    } catch {
      setCancelReasonData(CANCEL_REASONS);
      reasonsLoadedRef.current = true;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    try {
      let errObj;
      const selectedReason = cancelReason === 'OTHER' ? otherReason : cancelReason;

      if (!selectedReason || selectedReason.length < 1)
        errObj = { error: true, errorType: 'REQUIRED', errorMsg: strings.requiredText };
      else if (selectedReason.length > maxLength)
        errObj = {
          error: true,
          errorType: 'MAX_LENGTH',
          errorMsg: strings.formatString(strings.cancel_reason_length_error, maxLength),
        };
      else errObj = { error: false, errorType: '', errorMsg: '' };

      if (errObj.error) {
        setError(errObj);
      } else {
        setIsLoading(true);
        const body = {
          cancelReason: selectedReason,
          status: 'CANCELLED',
        };

        await axiosInstance.put(URLS.cancelBookingLineItem(id), body);
        setMessage({
          display: true,
          severity: 'success',
          message: strings.bookingCancelSuccess,
        });
        handleClose();
        onSuccess();
      }
    } catch (error: any) {
      handleClose();
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setCancelReason('');
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {compact ? (
        <MenuItem
          onClick={openCancelDialog}
          sx={{ py: 0.75, gap: 1, fontSize: '0.875rem', fontWeight: 500, color: 'error.main' }}>
          <Cancel sx={{ fontSize: 18 }} />
          {strings.BookingDetailButonTextCancel}
        </MenuItem>
      ) : (
        <Button
          size="small"
          onClick={openCancelDialog}
          variant="contained"
          color="error"
          sx={{ ...bookingDetailToolbarButtonSx }}
          startIcon={<Cancel />}>
          {strings.BookingDetailButonTextCancel}
        </Button>
      )}
      <FormDialog
        open={openDialog}
        handleClose={handleClose}
        tone="error"
        headerIcon={<Cancel sx={{ color: 'inherit' }} />}
        title={strings.bookingDetailsCancelBooking}
        contentText={strings.bookingDetailsModalBodyMessageCancelBooking}
        saveButtonText={strings.bookingDetailsCancelBooking}
        loading={isLoading}
        handleSave={handleSave}
        disabledCondition={!cancelReason || (cancelReason === 'OTHER' && !otherReason)}>
        <Select
          error={error.error || !cancelReason || (cancelReason === 'OTHER' && !otherReason)}
          sx={{
            width: '100%',
            marginTop: '5px',
            '& .MuiFormHelperText-root': {
              textAlign: 'end',
              m: 0,
            },
          }}
          value={cancelReason}
          onChange={e => {
            setCancelReason(e.target.value);
            setOtherReason('');
            let errObj;
            if (e.target.value.length < 1)
              errObj = { error: true, errorType: 'REQUIRED', errorMsg: strings.requiredText };
            else if (e.target.value.length > maxLength)
              errObj = {
                error: true,
                errorType: 'MAX_LENGTH',
                errorMsg: strings.formatString(strings.cancel_reason_length_error, maxLength),
              };
            else errObj = { error: false, errorType: '', errorMsg: '' };
            setError(errObj);
          }}>
          {cancelReasonData.map(reason => (
            <MenuItem key={reason.id} value={reason.id}>
              {reason.name}
            </MenuItem>
          ))}
        </Select>
        {cancelReason === 'OTHER' && (
          <TextField
            error={error.error || !otherReason}
            sx={{
              width: '100%',
              marginTop: '5px',
              '& .MuiFormHelperText-root': {
                textAlign: 'end',
                m: 0,
              },
            }}
            maxRows={6}
            minRows={6}
            onChange={e => {
              setOtherReason(e.target.value);
              let errObj;
              if (e.target.value.length < 1)
                errObj = { error: true, errorType: 'REQUIRED', errorMsg: strings.requiredText };
              else if (e.target.value.length > maxLength)
                errObj = {
                  error: true,
                  errorType: 'MAX_LENGTH',
                  errorMsg: strings.formatString(strings.cancel_reason_length_error, maxLength),
                };
              else errObj = { error: false, errorType: '', errorMsg: '' };
              setError(errObj);
            }}
            multiline={true}
            value={otherReason}
            helperText={
              <Box display={'flex'}>
                {error?.errorMsg ? (
                  <Typography fontSize={'12px'}>{error.errorMsg}</Typography>
                ) : !otherReason ? (
                  <Typography fontSize={'12px'}>{strings.validateStringErrMsg}</Typography>
                ) : (
                  ''
                )}
              </Box>
            }
          />
        )}
      </FormDialog>
    </>
  );
};
